import { useSocialLinks } from 'app/hooks/use-social-links';
import type { MutationOptions } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bffUserProfileLoadedAtom, hasProfileAtom, isLoggedInAtom, selfUserLoadedAtom, userInfoAtom } from 'app/atoms';
import { asFormData } from 'app/lib/form';
import type { FunctionalUseQueryOptions } from 'app/types/network';
import type { User } from 'app/types/user';
import type { AxiosError } from 'axios';
import { useAtomValue, useSetAtom } from 'jotai';
import { http } from '../lib/axios';
import { getBffQueryData, useQueryLPC } from 'app/lib/query';
import { groupKeys, listingKeys, profileKeys } from './keys';
import { useQueryKeys } from './invalidation';
import type {
  Profile,
  CreateProfileDTO,
  UpdateProfileDTO,
  UpdateProfileUnavailabilityDTO,
  ProfileSharesResponse,
  TrustMember,
} from 'app/types/profile';

export type { Profile, CreateProfileDTO, UpdateProfileDTO, UpdateProfileUnavailabilityDTO } from 'app/types/profile';

type ProfileQueryOptions = FunctionalUseQueryOptions<Profile, AxiosError> & {
  useBff?: boolean;
};

export function useProfile(userId?: User['id'], { useBff = true, ...opts }: ProfileQueryOptions = {}) {
  const client = useQueryClient();
  const bffUserProfileLoaded = useAtomValue(bffUserProfileLoadedAtom);
  const queryKey = useBff ? profileKeys.detail(userId!) : profileKeys.privateDetail(userId!);
  return useQueryLPC<Profile, AxiosError>({
    queryKey,
    queryFn: async () =>
      (useBff && bffUserProfileLoaded ? getBffQueryData<Profile>(client, queryKey) : undefined) ??
      (
        await http.get<Profile>(`/profiles/${userId!}`, {
          params: { typeOfUserId: typeof userId, bffUserProfileLoaded },
        })
      ).data,
    ...opts,
    enabled: Boolean(userId) && (opts.enabled ?? true) && (!useBff || bffUserProfileLoaded),
    retry: (failureCount, error) => {
      // 404 is "ok"; don't retry
      return failureCount < (error.response?.status === 404 ? 0 : 4);
    },
  });
}

export function useMyProfile(opts: FunctionalUseQueryOptions<Profile, AxiosError> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const hasProfile = useAtomValue(hasProfileAtom);
  const userInfo = useAtomValue(userInfoAtom);
  const query = useProfile(userInfo?.id, {
    ...opts,
    enabled: isLoggedIn && !hasProfile && !!userInfo,
  });
  return query;
}

/** Fetches the authenticated user's complete profile, including private fields omitted from public/BFF DTOs. */
export function useMyPrivateProfile(opts: FunctionalUseQueryOptions<Profile, AxiosError> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const userInfo = useAtomValue(userInfoAtom);
  return useProfile(userInfo?.id, {
    ...opts,
    enabled: isLoggedIn && !!userInfo && (opts.enabled ?? true),
    useBff: false,
  });
}

export function useUpsertMyProfile(opts: MutationOptions<Profile, Error, CreateProfileDTO | UpdateProfileDTO> = {}) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  const setSelfUserLoaded = useSetAtom(selfUserLoadedAtom);

  return useMutation<Profile, Error, CreateProfileDTO | UpdateProfileDTO>({
    mutationFn: (payload) => http.patch<Profile>('/profiles', asFormData(payload)).then((res) => res.data),

    ...opts,
    async onSuccess(result, variables, context) {
      // A poll started before this save must not restore the previous reminder cycle.
      await client.cancelQueries({ queryKey: profileKeys.privateDetail(result.userId) });
      client.setQueryData(profileKeys.detail(result.userId), result);
      client.setQueryData(profileKeys.privateDetail(result.userId), result);
      await qk.selfUser.invalidate();
      setSelfUserLoaded(false);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useUpdateMyProfileUnavailability(
  opts: MutationOptions<Profile, Error, UpdateProfileUnavailabilityDTO> = {},
) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  const setSelfUserLoaded = useSetAtom(selfUserLoadedAtom);

  return useMutation<Profile, Error, UpdateProfileUnavailabilityDTO>({
    mutationFn: (payload) => http.patch<Profile>('/profiles/unavailability', payload).then((res) => res.data),

    ...opts,
    async onSuccess(result, variables, context) {
      client.setQueryData(profileKeys.detail(result.userId), result);
      client.setQueryData(profileKeys.privateDetail(result.userId), result);
      await qk.selfUser.invalidate();
      setSelfUserLoaded(false);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useDeleteMyProfileUnavailability(opts: MutationOptions<Profile> = {}) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  const setSelfUserLoaded = useSetAtom(selfUserLoadedAtom);

  return useMutation<Profile>({
    mutationFn: () => http.delete<Profile>('/profiles/unavailability').then((res) => res.data),

    ...opts,
    async onSuccess(result, variables, context) {
      client.setQueryData(profileKeys.detail(result.userId), result);
      client.setQueryData(profileKeys.privateDetail(result.userId), result);
      await qk.selfUser.invalidate();
      setSelfUserLoaded(false);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useAcknowledgeFestivalReminder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { cycleId: string; event: 'modal_seen' | 'banner_dismissed' }) =>
      (await http.patch<Profile>('/profiles/festival-reminder', payload)).data,
    retry: 3,
    onSuccess: async (result) => {
      // A delayed acknowledgement must not overwrite a more recent date edit.
      // Refresh the private source of reminder state instead of merging its snapshot.
      await client.invalidateQueries({ queryKey: profileKeys.privateDetail(result.userId) });
    },
  });
}

export interface ProfileSharesFilters {
  scope?: 'all' | 'common';
  role?: 'all' | 'lender' | 'borrower';
  take?: number;
}

export function useProfileShares(
  userId?: User['id'],
  filters: ProfileSharesFilters = {},
  opts: FunctionalUseQueryOptions<ProfileSharesResponse> = {},
) {
  const normalizedFilters = {
    scope: filters.scope ?? 'all',
    role: filters.role ?? 'all',
    take: filters.take ?? 50,
  } as const;
  return useQueryLPC<ProfileSharesResponse>({
    queryKey: profileKeys.shares(userId!, normalizedFilters),
    queryFn: () =>
      http
        .get<ProfileSharesResponse>(`/profiles/${userId!}/shares`, {
          params: normalizedFilters,
        })
        .then((res) => res.data),
    ...opts,
    enabled: Boolean(userId) && (opts.enabled ?? true),
  });
}

export function useTrustedBy(userId?: User['id'], opts: FunctionalUseQueryOptions<TrustMember[]> = {}) {
  const socialLinks = useSocialLinks();
  const query = useQueryLPC<TrustMember[]>({
    queryKey: profileKeys.trustedBy(userId!),
    queryFn: () => http.get<TrustMember[]>(`/profiles/${userId!}/trusted-by`).then((res) => res.data),
    ...opts,
    enabled: socialLinks.enabled && Boolean(userId) && (opts.enabled ?? true),
  });
  return { ...query, data: socialLinks.enabled ? query.data : undefined };
}

export function useTrustCircle(opts: FunctionalUseQueryOptions<TrustMember[]> = {}) {
  const socialLinks = useSocialLinks();
  const viewer = useAtomValue(userInfoAtom);
  const loggedIn = useAtomValue(isLoggedInAtom);
  const query = useQueryLPC<TrustMember[]>({
    queryKey: profileKeys.trustCircleFor(viewer?.id),
    queryFn: () => http.get<TrustMember[]>('/profiles/trust-circle/members').then((res) => res.data),
    ...opts,
    enabled: socialLinks.enabled && loggedIn && Boolean(viewer?.id) && (opts.enabled ?? true),
  });
  return { ...query, data: socialLinks.enabled ? query.data : undefined };
}

export function useAddTrustedMember(opts: MutationOptions<NonNullable<Profile['trust']>, Error, User['id']> = {}) {
  const socialLinks = useSocialLinks();
  const client = useQueryClient();
  return useMutation<NonNullable<Profile['trust']>, Error, User['id']>({
    ...opts,
    mutationFn: (userId) => {
      if (!socialLinks.enabled) throw new Error('Social links are not enabled.');
      return http.post<NonNullable<Profile['trust']>>(`/profiles/${userId}/trust`).then((res) => res.data);
    },
    onSuccess(result, userId, context) {
      client.setQueryData<Profile>(profileKeys.detail(userId), (profile) =>
        profile ? { ...profile, trust: result } : profile,
      );
      void Promise.all([
        client.invalidateQueries({ queryKey: profileKeys.trustedBy(userId) }),
        client.invalidateQueries({ queryKey: profileKeys.trustCircle() }),
        client.invalidateQueries({ queryKey: groupKeys.all }),
        client.invalidateQueries({ queryKey: listingKeys.all }),
      ]);
      return opts.onSuccess?.(result, userId, context);
    },
  });
}

export function useRemoveTrustedMember(opts: MutationOptions<undefined, Error, User['id']> = {}) {
  const socialLinks = useSocialLinks();
  const client = useQueryClient();
  return useMutation<undefined, Error, User['id']>({
    ...opts,
    mutationFn: (userId) => {
      if (!socialLinks.enabled) throw new Error('Social links are not enabled.');
      return http.delete(`/profiles/${userId}/trust`).then(() => undefined);
    },
    onSuccess(result, userId, context) {
      void Promise.all([
        client.invalidateQueries({ queryKey: profileKeys.detail(userId) }),
        client.invalidateQueries({ queryKey: profileKeys.trustedBy(userId) }),
        client.invalidateQueries({ queryKey: profileKeys.trustCircle() }),
        client.invalidateQueries({ queryKey: groupKeys.all }),
        client.invalidateQueries({ queryKey: listingKeys.all }),
      ]);
      return opts.onSuccess?.(result, userId, context);
    },
  });
}
